import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "@mui/material";
import { SovosBox, SovosPill, SovosTypography } from "mosaic-react";
import { DataGrid } from "colbi_web_ui/lib/components";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import { trackUserAction } from "../../../../utils/pendo";
import {
    buildManualErrorsByColumn,
    buildSuggestionsByColumn,
    fixKey,
    resolveStagingCellHighlight,
} from "./aiFixApplication";
import {
    getStagingCellDisplayValue,
    isStagingSuggestionCellEditable,
    stagingCellValueGetter,
    stagingCellValueSetter,
} from "./stagingGridCellValue";
import { buildCellIssuesByKey, errorRowIdKeysFromIssues, filterStagingItemsToErrorRows } from "./stagingIssuePresentation";
import StagingDataCellCard from "./StagingDataCellCard";
import StagingDataToolbar from "./StagingDataToolbar";
import SuggestedFixesStatTile from "./SuggestedFixesStatTile";
import { toSourceColumnLabel } from "../mappingDisplayLabels";

const HEADER_LABEL_ROW_HEIGHT = 32;
const STAGING_COLUMN_WIDTH = 220;

const buildStagingGridHeaderSx = (theme) => ({
    "& .MuiDataGrid-columnHeader": {
        alignItems: "stretch",
        backgroundColor: `${theme.palette.background.paper} !important`,
        p: 0,
    },
    "& .MuiDataGrid-columnHeaderDraggableContainer": {
        alignItems: "stretch",
        display: "flex",
        height: "100%",
        width: "100%",
    },
    "& .MuiDataGrid-columnHeaderTitleContainer": {
        height: "100%",
        minHeight: HEADER_LABEL_ROW_HEIGHT,
    },
    "& .MuiDataGrid-columnHeaderTitleContainerContent": {
        height: "100%",
        width: "100%",
    },
    "& .MuiDataGrid-columnHeaders [aria-rowindex='1']": {
        borderBottom: `1px solid ${theme.palette.divider}`,
        boxSizing: "border-box",
    },
    "& .MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitleContainer": {
        borderBottom: "none !important",
        boxSizing: "border-box",
        justifyContent: "center",
    },
    "& .MuiDataGrid-columnHeader--filledGroup .MuiDataGrid-columnHeaderTitleContainerContent": {
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
    },
    "& .MuiDataGrid-columnHeader--emptyGroup .MuiDataGrid-columnHeaderTitleContainer": {
        borderBottom: "none !important",
        boxSizing: "border-box",
    },
    "& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--filledGroup):not(.MuiDataGrid-columnHeader--emptyGroup) .MuiDataGrid-columnHeaderTitleContainerContent": {
        alignItems: "center",
        display: "flex",
        px: 1,
    },
});

const STAGING_PILL_TEXT_COLORS = {
    error: "error.main",
    success: "success.dark",
    warning: "warning.dark",
};

const StagingColumnStatusPill = ({ kind, label }) => (
    <SovosPill
        color={kind}
        sx={{
            height: 20,
            minHeight: 20,
            px: 1,
        }}
    >
        <SovosTypography
            component="span"
            sx={{
                color: STAGING_PILL_TEXT_COLORS[kind],
                fontSize: 12,
                fontWeight: 600,
                lineHeight: 1,
            }}
        >
            {label}
        </SovosTypography>
    </SovosPill>
);

const getColumnIssueStats = (headerItem, byColumn, manualErrorsByColumn, suggestionsByColumn) => {
    const colName = headerItem.columnName;
    const bareCol = colName?.replace(/^CDM_[^.]+\./, "") || colName;
    const stats = byColumn[colName] || byColumn[bareCol] || null;
    const suggestionCount = suggestionsByColumn.get(bareCol) || 0;
    const errorCount = manualErrorsByColumn.get(bareCol)
        || manualErrorsByColumn.get(colName)
        || 0;
    const warningCount = stats?.warnings || 0;
    const hasIssues = suggestionCount > 0 || errorCount > 0 || warningCount > 0;

    return {
        errorCount,
        hasIssues,
        suggestionCount,
        warningCount,
    };
};

const StagingColumnStatusBadges = ({ errorCount, hasIssues, i18n, suggestionCount, warningCount }) => (
    <SovosBox
        sx={{
            alignItems: "center",
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            height: "100%",
            justifyContent: "center",
            minHeight: HEADER_LABEL_ROW_HEIGHT,
            px: 0.5,
            width: "100%",
        }}
    >
        {suggestionCount > 0 && (
            <StagingColumnStatusPill
                kind="warning"
                label={`${suggestionCount} ${i18n(suggestionCount === 1
                    ? "Column issue suggestion singular"
                    : "Column issue suggestion plural")}`}
            />
        )}
        {errorCount > 0 && (
            <StagingColumnStatusPill
                kind="error"
                label={`${errorCount} ${i18n(errorCount === 1
                    ? "Column issue error singular"
                    : "Column issue error plural")}`}
            />
        )}
        {warningCount > 0 && (
            <StagingColumnStatusPill
                kind="warning"
                label={`${warningCount} ${i18n(warningCount === 1
                    ? "Column issue warning singular"
                    : "Column issue warning plural")}`}
            />
        )}
        {!hasIssues && (
            <StagingColumnStatusPill
                kind="success"
                label={`0 ${i18n`Column issues plural`}`}
            />
        )}
    </SovosBox>
);

const StatTile = ({ label, value, borderColor, hint }) => (
    <SovosBox
        sx={{
            flex: 1,
            minWidth: 140,
            p: 1.5,
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            borderBottom: "4px solid",
            borderBottomColor: borderColor,
            display: "flex",
            flexDirection: "column",
            gap: 0.25,
            bgcolor: "background.paper",
        }}
    >
        <SovosTypography variant="caption" sx={{ color: "text.secondary" }}>{label}</SovosTypography>
        <SovosTypography variant="h6" sx={{ fontWeight: 700 }}>{value}</SovosTypography>
        {hint && <SovosTypography variant="caption" sx={{ color: "text.disabled" }}>{hint}</SovosTypography>}
    </SovosBox>
);

const StagingDataGrid = ({
    items = [],
    columnHeaders = [],
    issueSummary = null,
    loading = false,
    savingCell = false,
    profileId,
    projectId,
    canMutate = true,
    onFixCell,
    onRefreshIssues,
    aiSuggestionsEnabled = false,
    hasAiFixResults = false,
    onAcceptAllSuggestedFixes,
    onOpenAiPanel,
    onRejectAllSuggestedFixes,
    autoApplicableFixCount = 0,
    suggestedFixCount = 0,
    suggestionCellKeys = new Set(),
    suggestionDetailsByKey = new Map(),
    onAcceptSuggestion,
    onFinalizeSuggestionAccept,
    onRejectSuggestion,
}) => {
    const { i18n } = useGlobalValue();
    const theme = useTheme();
    const [showErrorsOnly, setShowErrorsOnly] = useState(false);
    const [rowOverrides, setRowOverrides] = useState(new Map());
    const [isSavingLocal, setIsSavingLocal] = useState(false);

    const byColumn = useMemo(() => issueSummary?.by_column || {}, [issueSummary]);

    const allRowsCount = issueSummary?.affected_rows != null
        ? items.length
        : items.length;
    const totalErrors = issueSummary?.total_errors ?? 0;
    const affectedRows = issueSummary?.affected_rows ?? 0;
    const validRows = Math.max(0, allRowsCount - affectedRows);

    useEffect(() => {
        setRowOverrides(new Map());
    }, [items]);

    useEffect(() => {
        if (totalErrors === 0) setShowErrorsOnly(false);
    }, [totalErrors]);

    const issuesByClbId = useMemo(() => {
        const map = new Map();
        const rowsArr = issueSummary?.rows || [];
        for (const row of rowsArr) {
            map.set(row.clb_id, row.issues || []);
        }
        return map;
    }, [issueSummary]);

    const suggestionsByColumn = useMemo(
        () => buildSuggestionsByColumn(suggestionCellKeys),
        [suggestionCellKeys],
    );

    const manualErrorsByColumn = useMemo(
        () => buildManualErrorsByColumn(issueSummary?.rows || [], suggestionCellKeys),
        [issueSummary, suggestionCellKeys],
    );

    const cellIssuesByKey = useMemo(
        () => buildCellIssuesByKey(issueSummary?.rows || [], suggestionCellKeys),
        [issueSummary, suggestionCellKeys],
    );

    const errorRowIdKeys = useMemo(() => {
        if (!showErrorsOnly) return null;
        return errorRowIdKeysFromIssues(issueSummary?.rows);
    }, [issueSummary, showErrorsOnly]);

    const effectiveItems = useMemo(() => {
        const withOverrides = rowOverrides.size === 0
            ? items
            : items.map((item) => {
                const id = item.CLB_ID ?? item.clb_id;
                const override = rowOverrides.get(String(id));
                return override ? { ...item, ...override } : item;
            });
        return filterStagingItemsToErrorRows(withOverrides, errorRowIdKeys);
    }, [items, rowOverrides, errorRowIdKeys]);

    const gridItems = useMemo(() => {
        const shouldWrapErrors = cellIssuesByKey.size > 0;
        if (suggestionCellKeys.size === 0 && !shouldWrapErrors) return effectiveItems;
        return effectiveItems.map((item) => {
            const clbId = item.CLB_ID ?? item.clb_id;
            const next = { ...item };
            for (const header of columnHeaders) {
                const field = header.columnName;
                const bareColumn = field.replace(/^CDM_[^.]+\./, "");
                const key = fixKey(clbId, bareColumn);
                if (suggestionCellKeys.has(key)) {
                    next[field] = {
                        displayValue: getStagingCellDisplayValue(item, field),
                        stagingCellKind: "aiSuggestion",
                        suggestionKey: key,
                    };
                    continue;
                }
                if (shouldWrapErrors && cellIssuesByKey.has(key)) {
                    next[field] = {
                        displayValue: getStagingCellDisplayValue(item, field),
                        issueKey: key,
                        stagingCellKind: "error",
                    };
                }
            }
            return next;
        });
    }, [cellIssuesByKey, columnHeaders, effectiveItems, suggestionCellKeys]);

    const cellCard = useMemo(
        () => (props) => (
            <StagingDataCellCard
                {...props}
                onAcceptSuggestion={onAcceptSuggestion}
                onFinalizeSuggestionAccept={onFinalizeSuggestionAccept}
                onRejectSuggestion={onRejectSuggestion}
                suggestionDetailsByKey={suggestionDetailsByKey}
            />
        ),
        [
            onAcceptSuggestion,
            onFinalizeSuggestionAccept,
            onRejectSuggestion,
            suggestionDetailsByKey,
        ],
    );

    const columnHeaderRenderer = useCallback((headerItem) => (
        <SovosBox sx={{ alignItems: "center", display: "flex", height: "100%", width: "100%" }}>
            <SovosTypography variant="caption" sx={{ fontWeight: 600 }} noWrap>
                {toSourceColumnLabel(headerItem.headerName || headerItem.columnName)}
            </SovosTypography>
        </SovosBox>
    ), []);

    const columnGroupingModel = useMemo(
        () => columnHeaders.map((headerItem) => {
            const field = headerItem.columnName;
            const stats = getColumnIssueStats(
                headerItem,
                byColumn,
                manualErrorsByColumn,
                suggestionsByColumn,
            );

            return {
                children: [{ field }],
                groupId: field,
                headerAlign: "center",
                renderHeaderGroup: () => (
                    <StagingColumnStatusBadges
                        {...stats}
                        i18n={i18n}
                    />
                ),
            };
        }),
        [byColumn, columnHeaders, i18n, manualErrorsByColumn, suggestionsByColumn],
    );

    const cellHighlight = useCallback((params) => {
        const clbId = params.row?.CLB_ID ?? params.row?.clb_id;
        const bareColumn = params.field.replace(/^CDM_[^.]+\./, "");
        return resolveStagingCellHighlight({
            bareColumn,
            clbId,
            issuesByClbId,
            suggestionCellKeys,
        });
    }, [issuesByClbId, suggestionCellKeys]);

    const isCellEditable = useCallback((params) => {
        if (!canMutate) return false;
        return isStagingSuggestionCellEditable(params, suggestionCellKeys);
    }, [canMutate, suggestionCellKeys]);

    const handleRowEdit = useCallback(async (newRow, oldRow) => {
        if (!canMutate) return oldRow;
        const changedCol = columnHeaders
            .map((h) => h.columnName)
            .find((col) => getStagingCellDisplayValue(newRow, col) !== getStagingCellDisplayValue(oldRow, col));
        if (!changedCol) return newRow;
        const clbId = newRow.CLB_ID ?? newRow.clb_id ?? newRow["CDM_CustomCSV.CLB_ID"];
        const bareColumn = changedCol.replace(/^CDM_[^.]+\./, "");
        if (suggestionCellKeys.has(fixKey(clbId, bareColumn))) return oldRow;
        if (!onFixCell || !profileId || !projectId || clbId == null) return oldRow;
        setIsSavingLocal(true);
        try {
            const result = await onFixCell(
                profileId,
                String(projectId),
                clbId,
                bareColumn,
                getStagingCellDisplayValue(newRow, changedCol) ?? null,
            );
            if (!result?.success) return oldRow;
            trackUserAction("cleansing_exception_resolved", {
                profile_id: profileId,
                column: bareColumn,
                clb_id: clbId,
                mismatch_resolved: false,
                source: "staging_panel",
            });
            setRowOverrides((prev) => {
                const next = new Map(prev);
                next.set(String(clbId), newRow);
                return next;
            });
            if (onRefreshIssues) onRefreshIssues();
            return newRow;
        } catch (_) {
            return oldRow;
        } finally {
            setIsSavingLocal(false);
        }
    }, [canMutate, columnHeaders, onFixCell, profileId, projectId, onRefreshIssues, suggestionCellKeys]);

    const dgHeader = useMemo(
        () => columnHeaders.map((h) => {
            const usesCustomCells = suggestionCellKeys.size > 0
                || cellIssuesByKey.size > 0;
            return {
                ...h,
                disableColumnMenu: true,
                label: [toSourceColumnLabel(h.headerName || h.columnName)],
                minWidth: STAGING_COLUMN_WIDTH,
                sortable: false,
                width: STAGING_COLUMN_WIDTH,
                editable: canMutate,
                type: usesCustomCells ? "customComponent" : undefined,
                ...(usesCustomCells ? {
                    valueGetter: stagingCellValueGetter,
                    valueSetter: stagingCellValueSetter,
                } : {}),
            };
        }),
        [canMutate, cellIssuesByKey.size, columnHeaders, suggestionCellKeys.size],
    );

    const aiDisabledReason = !aiSuggestionsEnabled
        ? i18n`Sovi AI suggestions are not enabled for this project.`
        : "";

    return (
        <SovosBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <SovosBox sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                <StatTile label={i18n`All rows`} value={allRowsCount} borderColor="primary.main" />
                <StatTile label={i18n`Valid rows`} value={validRows} borderColor="success.main" />
                <StatTile label={i18n`Errors`} value={totalErrors} borderColor="error.main" />
                <SuggestedFixesStatTile
                    hint={aiSuggestionsEnabled ? undefined : i18n`Available with Sovi AI`}
                    onAcceptAll={onAcceptAllSuggestedFixes}
                    onRejectAll={onRejectAllSuggestedFixes}
                    showActions={aiSuggestionsEnabled && hasAiFixResults && autoApplicableFixCount > 0}
                    value={aiSuggestionsEnabled ? suggestedFixCount : 0}
                />
            </SovosBox>

            <SovosBox sx={{ maxWidth: "100%", minWidth: 0, overflowX: "auto", width: "100%" }}>
                <DataGrid
                    id="staging-data-grid"
                    isCellEditable={isCellEditable}
                    items={gridItems}
                    header={dgHeader}
                    card={cellCard}
                    status={{ loading: loading }}
                    metadata={{ total: gridItems.length }}
                    enablePagination
                    columnGroupingModel={columnGroupingModel}
                    headerHeight={HEADER_LABEL_ROW_HEIGHT}
                    columnHeaderRenderer={columnHeaderRenderer}
                    showCheckbox
                    editMode={canMutate}
                    onRowEdit={handleRowEdit}
                    onCellHighlight={cellHighlight}
                    toolbar={StagingDataToolbar}
                    toolbarProps={{
                        aiDisabledReason,
                        aiSuggestionsEnabled,
                        isSaving: savingCell || isSavingLocal,
                        loading,
                        onOpenAiPanel,
                        onToggleErrors: () => setShowErrorsOnly((value) => !value),
                        showErrorsDisabled: !issueSummary || totalErrors === 0,
                        showErrorsOnly,
                    }}
                    modifierssx={{
                        ...buildStagingGridHeaderSx(theme),
                        "& .MuiDataGrid-main": { minWidth: 0, overflowX: "auto" },
                        "& .MuiDataGrid-virtualScroller": { overflowX: "auto" },
                        "& .staging-warning-cell": {
                            backgroundColor: `${theme.palette.warning.light} !important`,
                            borderLeft: `3px solid ${theme.palette.warning.main} !important`,
                        },
                        "& .staging-ai-suggestion-cell": {
                            backgroundColor: `${theme.palette.warning.light} !important`,
                        },
                        "& .staging-error-cell": {
                            backgroundColor: `${theme.palette.error.light} !important`,
                            borderLeft: `3px solid ${theme.palette.error.main} !important`,
                        },
                    }}
                />
            </SovosBox>
        </SovosBox>
    );
};

export default StagingDataGrid;
